import { Button } from "@/components/ui/button";
import { formatSubscriptionDate } from "@/lib/dateUtils";
import SubscriptionSkeleton from "./SubscriptionSkeleton";
import { useGetSubscriptionOverview } from "@/features/tokens/api/token.queries";
import type { ISubscription } from "@/features/tokens/types/token.types";

// You can use simple platform icons (using RemixIcon)
const platformIcons: Record<string, string> = {
  facebook: "/logo/fb-logo.svg",
  twitter: "/logo/x-logo.svg",
  youtube: "/logo/yt-logo.svg",
  tiktok: "/logo/tiktok-logo.svg",
  reddit: "/logo/reddit-logo.svg",
  instagram: "/logo/ig-logo.svg",
};

export default function ManagerSubscription() {
  const subscriptionOverview = useGetSubscriptionOverview();
  const subscriptionData = subscriptionOverview.data;
  const isLoading = subscriptionOverview.isLoading;
  const isError = subscriptionOverview.isError;

  if (isError) {
    return (
      <div className="flex w-full flex-col gap-5 rounded-lg bg-white p-5">
        <div className="flex flex-row items-center justify-between">
          <div className="space-y-2">
            <div className="text-lg leading-5 font-bold -tracking-wide text-neutral-950">
              Subscription Plan
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (isLoading) {
    return <SubscriptionSkeleton />;
  }

  const subscriptions = subscriptionData?.active_subscriptions ?? [];

  return (
    <div className="flex w-full flex-col gap-5 rounded-lg bg-white p-5">
      {/* Header Section */}
      <div className="flex flex-row items-center justify-between">
        <div className="space-y-2">
          <div className="text-lg leading-5 font-bold -tracking-wide text-neutral-950">
            Subscription Plan
          </div>
          <div className="text-xs leading-3 text-neutral-500">
            Manage your subscription
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <Button
            variant="minimal"
            icon
            className="text-muted hover:text-muted flex flex-row items-center gap-1.5 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
          >
            <i className="ri-loop-right-line text-md font-light"></i>
            Renew
          </Button>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div
        className="scrollbar-thin overflow-x-auto"
        style={{ scrollbarGutter: "stable" }}
      >
        <div className="min-w-[800px]">
          {/* Table Header */}
          <div className="grid grid-cols-[minmax(250px,2fr)_minmax(180px,1.5fr)_minmax(150px,1.5fr)_minmax(150px,1.5fr)_minmax(100px,1fr)_minmax(100px,1fr)] gap-4 px-4 py-3">
            <div className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
              Plan
            </div>
            <div className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
              Platforms
            </div>
            <div className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
              Started
            </div>
            <div className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
              Until
            </div>
            <div className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
              Quota
            </div>
            <div className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
              Calls/Min
            </div>
          </div>

          {/* Table Rows */}
          {subscriptions.map((sub: ISubscription) => (
            <div
              key={sub.subscription_id}
              className="grid grid-cols-[minmax(250px,2fr)_minmax(180px,1fr)_minmax(150px,1.5fr)_minmax(150px,1.5fr)_minmax(100px,1fr)_minmax(100px,1fr)] items-center gap-4 rounded-lg px-4 py-4"
            >
              {/* Plan name */}
              <div className="truncate text-sm font-bold text-neutral-950">
                {sub.plan_name}
              </div>

              {/* Platform icons - prioritized with minimum width */}
              <div className="flex flex-row items-center gap-2 overflow-visible">
                {sub.selected_platforms?.length > 0 ? (
                  sub.selected_platforms
                    ?.sort((a: string, b: string) => a.localeCompare(b))
                    .map((platform: string) => (
                      <img
                        key={platform}
                        src={platformIcons[platform]}
                        alt={platform}
                        className="h-5 w-5 flex-shrink-0"
                        onError={(e) => {
                          // Hide broken images
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ))
                ) : (
                  <h1 className="text-sm text-neutral-950">N/A</h1>
                )}
              </div>

              {/* Start date */}
              <div className="truncate text-sm text-neutral-950">
                {formatSubscriptionDate(sub.subscription_start)}
              </div>

              {/* End date */}
              <div className="truncate text-sm text-neutral-950">
                {formatSubscriptionDate(sub.subscription_end)}
              </div>

              {/* Quota */}
              <div className="truncate text-sm text-neutral-950">
                {sub.billing ? (
                  <span className="block truncate text-xs">
                    Tier: {sub.billing.charge_breakdown?.[0]?.tier ?? "N/A"}
                  </span>
                ) : (
                  <span>{sub.quota_total?.toLocaleString() ?? "N/A"}</span>
                )}
              </div>

              {/* Calls per min */}
              <div className="truncate text-sm text-neutral-950">
                {sub.calls_per_min ?? "N/A"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
